import React, { useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import queryString from "query-string";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import moment from "moment";

import RetainerOrClientCareContractsFilters from "./RetainerOrClientCareContractsFilters";
import ReportService from "../../../services/reportService";

import { formatDate } from "../../../helpers/date";

import RetainerOrClientCareContractsList from "./RetainerOrClientCareContractsList";
import { downloadCSV } from "../utils";
import { RETAINER_OR_CLIENT_CARE_CONTRACTS_COLUMNS } from "./columns";

import * as exportActions from "../../../actions/exportActions";
import * as reportActions from "../../../actions/reportActions";
import * as codeActions from "../../../actions/codeActions";

const RetainerOrClientCareContractsPage = ({
  exported,
  exporting,
  actions,
  history,
  codes
}) => {
  const [loading, setLoading] = useState(false);
  const [pages, setPages] = useState(0);
  const [projects, setProjects] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [sorted, setSorted] = useState([{ id: "name", desc: true }]);

  const [filters, setFilters] = useState({
    page: 0,
    pageSize: 10,
    end_date: formatDate(new Date(), "yyyy-MM-dd")
  });

  const [data, setData] = useState([]);

  useEffect(() => {
    actions.loadCodes();
  }, [actions]);

  useEffect(() => {
    if (codes.length) {
      const activeStatus = codes.find(
        (code) =>
          code.entity === "Contract" &&
          code.field === "status" &&
          code.label === "Active"
      );
      if (activeStatus) {
        setFilters((prev) => ({
          ...prev,
          contract_status: [
            { label: activeStatus.value, value: activeStatus._id }
          ]
        }));
      }
    }
  }, [codes]);

  const fetchData = useCallback(
    async (pageNumber, size, rtSorted) => {
      setLoading(true);
      const newSorted = rtSorted || sorted;
      let query = {
        groupByField: "contract",
        ...newSorted.reduce((acc, curr) => {
          acc[`sort.${curr.id}`] = curr.desc ? 1 : -1;
          return acc;
        }, {})
      };
      if (filters.customer && filters.customer.length) {
        query.customer = filters.customer.map(({ value }) => value);
      }
      if (filters.contract_type && filters.contract_type.length) {
        query.contract_type = filters.contract_type.map(({ value }) => value);
      }
      if (filters.contract_status && filters.contract_status.length) {
        query.contract_status = filters.contract_status.map(
          ({ value }) => value
        );
      }
      if (filters.end_date) {
        query.end_date = filters.end_date;
      }
      if (filters.pageSize || size) {
        query.pageSize = size || filters.pageSize;
      }
      if (typeof filters.page === "number" || pageNumber) {
        query.page = pageNumber || filters.page;
      }
      query = queryString.stringify(query, { arrayFormat: "bracket" });
      const { data: result, pages: numPages } = await ReportService.get(
        `/retainer-or-client-care-contracts?${query}`
      );
      setData(result);
      setPages(numPages);
      setLoading(false);
    },
    [filters, setData, setLoading, sorted]
  );

  useEffect(() => {
    if (exported) {
      const url = window.URL.createObjectURL(
        new Blob([exported], { type: "application/pdf" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = `Retainer-Or-Client-Care-Contracts-${new Date().getTime()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      actions.clearExported();
    }
  }, [exported, actions]);

  const handleChange = (name) => (value) => {
    setLoading(false);
    setFilters({ ...filters, [name]: value });
  };

  const handleRowClick = (rowInfo) => {
    history.push(`/app/contracts/${rowInfo.original._id}`);
  };

  const handleSortedChange = async (rtSorted) => {
    setSorted(rtSorted);
    fetchData(null, null, rtSorted);
  };

  const handlePageSizeChange = async (pageSize) => {
    setFilters({ ...filters, pageSize });
    fetchData(null, pageSize);
  };

  const handlePageChange = async (page) => {
    setFilters({ ...filters, page });
    fetchData(page, null);
  };

  const handleExport = async () => {
    setLoading(true);
    const newSorted = sorted;
    let query = {
      ...newSorted.reduce((acc, curr) => {
        acc[`sort.${curr.id}`] = curr.desc ? 1 : -1;
        return acc;
      }, {})
    };

    if (filters.customer && filters.customer.length) {
      query.customer = filters.customer.map(({ value }) => value);
    }
    if (filters.contract_type && filters.contract_type.length) {
      query.contract_type = filters.contract_type.map(({ value }) => value);
    }
    if (filters.contract_status && filters.contract_status.length) {
      query.contract_status = filters.contract_status.map(({ value }) => value);
    }
    if (filters.end_date) {
      query.end_date = filters.end_date;
    }
    query.pageSize = null;
    query.page = null;
    query = queryString.stringify(query, { arrayFormat: "bracket" });
    const { data: result } = await ReportService.get(
      `/retainer-or-client-care-contracts?${query}`
    );

    const title = `Retainer/Client Care Contracts to Expire Through ${moment(
      filters.end_date
    ).format("MM/DD/YYYY")}`;

    downloadCSV(
      `RetainerOrClientCareContracts-${formatDate(moment(), "yyyyMMdd")}`,
      result,
      RETAINER_OR_CLIENT_CARE_CONTRACTS_COLUMNS,
      title
    );
    setLoading(false);
  };

  const handleExportPDF = async () => {
    const query = { ...filters, "sort.name": 1 };
    if (filters.customer && filters.customer.length) {
      query.customer = filters.customer.map(({ value }) => value);
    }
    if (filters.contract_type && filters.contract_type.length) {
      query.contract_type = filters.contract_type.map(({ value }) => value);
    }
    if (filters.contract_status && filters.contract_status.length) {
      query.contract_status = filters.contract_status.map(({ value }) => value);
    }
    // add sort by contract name using sorted var

    const payload = {
      data: query,
      type: "RETAINER_OR_CLIENT_CARE",
      format: "pdf"
    };

    actions.performExport(payload);
  };

  const handleResetFilters = () => {
    const activeStatus = codes.find(
      (code) =>
        code.entity === "Contract" &&
        code.field === "status" &&
        code.label === "Active"
    );
    setFilters({
      page: 0,
      pageSize: 10,
      end_date: formatDate(new Date(), "yyyy-MM-dd"),
      contract_status: [{ label: activeStatus.value, value: activeStatus._id }]
    });
    setData([]);
  };

  return (
    <div className="report-page">
      <RetainerOrClientCareContractsFilters
        filters={filters}
        onChange={handleChange}
        codes={codes}
        customers={customers}
        setProjects={setProjects}
        setCustomers={setCustomers}
        projects={projects}
        fetchData={fetchData}
        onExportClick={handleExport}
        onPDFExport={handleExportPDF}
        handleResetFilters={handleResetFilters}
        exportDisabled={loading || exporting || !data?.length}
        loading={loading}
      />
      <RetainerOrClientCareContractsList
        data={data}
        loading={loading}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onSortedChange={handleSortedChange}
        page={filters.page}
        pages={pages}
        sorted={sorted}
        pageSize={filters.pageSize}
        handleExport={handleExport}
        onRowClick={handleRowClick}
      />
    </div>
  );
};

RetainerOrClientCareContractsPage.propTypes = {
  history: PropTypes.object,
  codes: PropTypes.array,
  actions: PropTypes.object,
  exported: PropTypes.string,
  exporting: PropTypes.bool
};

RetainerOrClientCareContractsPage.defaultProps = {
  actions: {},
  codes: []
};
function mapStatesToProps(state) {
  return {
    globalState: state.reducers,
    exported: state.reducers.exported,
    exporting: state.reducers.exporting,
    codes: state.reducers.codes
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        ...exportActions,
        ...reportActions,
        ...codeActions
      },
      dispatch
    )
  };
}
export default connect(
  mapStatesToProps,
  mapDispatchToProps
)(RetainerOrClientCareContractsPage);
